import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Avatar, Divider, Tooltip } from '@mui/material';
import {
  Dashboard, People, CalendarMonth, Inventory2, Receipt,
  LocalHospital, Person, Logout, MonitorHeart, MedicalServices,
  Business
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

export const DRAWER_WIDTH = 240;

const navItems = [
  { label: 'Dashboard',    path: '/dashboard',    icon: <Dashboard sx={{ fontSize: 20 }} /> },
  { label: 'Appointments', path: '/appointments', icon: <CalendarMonth sx={{ fontSize: 20 }} /> },
  { label: 'Patients',     path: '/patients',     icon: <People sx={{ fontSize: 20 }} /> },
  { label: 'Doctors',      path: '/doctors',      icon: <MedicalServices sx={{ fontSize: 20 }} /> },
  { label: 'Departments',  path: '/departments',  icon: <Business sx={{ fontSize: 20 }} /> },
  { label: 'Inventory',    path: '/inventory',    icon: <Inventory2 sx={{ fontSize: 20 }} /> },
  { label: 'Billing',      path: '/billing',      icon: <Receipt sx={{ fontSize: 20 }} /> },
  { label: 'Staff',        path: '/staff',        icon: <Person sx={{ fontSize: 20 }} />, roles: ['superadmin','admin'] },
  { label: 'Hospitals',    path: '/hospitals',    icon: <LocalHospital sx={{ fontSize: 20 }} />, roles: ['superadmin'] },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const filtered = navItems.filter(i => !i.roles || i.roles.includes(user?.role || ''));

  return (
    <Box sx={{
      width: DRAWER_WIDTH, height: '100vh', position: 'fixed', display: 'flex', flexDirection: 'column',
      bgcolor: 'white', borderRight: '1px solid #f0fdfa',
      boxShadow: '4px 0 24px rgba(13,148,136,0.06)'
    }}>
      {/* Logo */}
      <Box sx={{ px: 3, py: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2.5, background: 'linear-gradient(135deg, #0d9488, #14b8a6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(13,148,136,0.35)' }}>
          <MonitorHeart sx={{ color: 'white', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#0f172a', lineHeight: 1 }}>MedelaHub</Typography>
          <Typography sx={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>HMS Platform</Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#f1f5f9' }} />

      {/* Nav */}
      <List sx={{ flex: 1, px: 1.5, pt: 1.5, overflowY: 'auto' }}>
        {filtered.map(item => {
          const active = location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2.5, py: 1.1, px: 1.5,
                  bgcolor: active ? '#f0fdfa' : 'transparent',
                  '&:hover': { bgcolor: active ? '#ccfbf1' : '#f8fafc' },
                  transition: 'all 0.15s',
                  position: 'relative',
                  '&::before': active ? {
                    content: '""', position: 'absolute', left: 0, top: '20%', height: '60%',
                    width: 3, borderRadius: '0 3px 3px 0', bgcolor: '#0d9488'
                  } : {}
                }}>
                <ListItemIcon sx={{ minWidth: 36, color: active ? '#0d9488' : '#94a3b8' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 13.5, fontWeight: active ? 700 : 500, color: active ? '#0d9488' : '#64748b' }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: '#f1f5f9' }} />

      {/* User */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2.5, bgcolor: '#f8fafc', border: '1px solid #f1f5f9' }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: '#ccfbf1', color: '#0d9488', fontSize: 14, fontWeight: 800 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }} noWrap>{user?.name}</Typography>
            <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{user?.role}</Typography>
          </Box>
          <Tooltip title="Logout">
            <Box onClick={logout} sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1.5, '&:hover': { bgcolor: '#fee2e2' }, transition: 'background 0.15s' }}>
              <Logout sx={{ fontSize: 16, color: '#94a3b8', '&:hover': { color: '#ef4444' } }} />
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
